// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';

export function isEntityModel(source: any): source is IEntity {
    // Method Implementation: simple
    return source.modelName === 'Entity';
}

export function isQuestionModel(source: any): source is IQuestion {
    // Method Implementation: simple
    return source.modelName === 'Question';
}

export function isStringQuestionModel(source: any): source is IStringQuestion {
    // Method Implementation: simple
    return source.modelName === 'StringQuestion';
}

export function isNumericQuestionModel(source: any): source is INumericQuestion {
    // Method Implementation: simple
    return source.modelName === 'NumericQuestion';
}

export function isBooleanQuestionModel(source: any): source is IBooleanQuestion {
    // Method Implementation: simple
    return source.modelName === 'BooleanQuestion';
}

export function isStringEnumerableQuestionModel(source: any): source is IStringEnumerableQuestion {
    // Method Implementation: simple
    return source.modelName === 'StringEnumerableQuestion';
}

export function isNumericEnumerableQuestionModel(source: any): source is INumericEnumerableQuestion {
    // Method Implementation: simple
    return source.modelName === 'NumericEnumerableQuestion';
}

export function isAnswerModel(source: any): source is IAnswer {
    // Method Implementation: simple
    return source.modelName === 'Answer';
}

export function isStringAnswerModel(source: any): source is IStringAnswer {
    // Method Implementation: simple
    return source.modelName === 'StringAnswer';
}

export function isNumericAnswerModel(source: any): source is INumericAnswer {
    // Method Implementation: simple
    return source.modelName === 'NumericAnswer';
}

export interface EntityZodSchemaShape {
    entityId?: string;

    questions?: QuestionZodSchemaShape[];
}

export interface QuestionZodSchemaShape {
    questionType?: string;

    name?: string;
}

export interface StringQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    value?: string;
}

export interface NumericQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    value?: number;
}

export interface BooleanQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    value?: boolean;
}

export interface StringEnumerableQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    value?: string;

    answers?: StringAnswerZodSchemaShape[];
}

export interface NumericEnumerableQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    value?: number;

    answers?: NumericAnswerZodSchemaShape[];
}

export interface AnswerZodSchemaShape {
    answerType?: string;

    name?: string;
}

export interface StringAnswerZodSchemaShape {
    answerType?: string;

    name?: string;

    value?: string;
}

export interface NumericAnswerZodSchemaShape {
    answerType?: string;

    name?: string;

    value?: number;
}

// Artifact Implementation: artifacts/zodSchemaType
export const EntityZodSchema: Zod.ZodSchema<EntityZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityId: Zod.string().optional(),
    questions: Zod.array(QuestionZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const QuestionZodSchema: Zod.ZodSchema<QuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringQuestionZodSchema: Zod.ZodSchema<StringQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericQuestionZodSchema: Zod.ZodSchema<NumericQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const BooleanQuestionZodSchema: Zod.ZodSchema<BooleanQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringEnumerableQuestionZodSchema: Zod.ZodSchema<StringEnumerableQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.string().optional(),
    answers: Zod.array(StringAnswerZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericEnumerableQuestionZodSchema: Zod.ZodSchema<NumericEnumerableQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.number().optional(),
    answers: Zod.array(NumericAnswerZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const AnswerZodSchema: Zod.ZodSchema<AnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringAnswerZodSchema: Zod.ZodSchema<StringAnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericAnswerZodSchema: Zod.ZodSchema<NumericAnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.number().optional()
}).nonstrict());

export interface IEntity extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityId: string;

    readonly questions: AQuestion[];

    readonly isEntityIdDefined: boolean;

    readonly isQuestionsDefined: boolean;
}

export interface IQuestion extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly isNameDefined: boolean;
}

export interface IStringQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export interface IBooleanQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value: boolean;

    readonly isValueDefined: boolean;
}

export interface IStringEnumerableQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly answers: StringAnswer[];

    readonly isValueDefined: boolean;

    readonly isAnswersDefined: boolean;
}

export interface INumericEnumerableQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly answers: NumericAnswer[];

    readonly isValueDefined: boolean;

    readonly isAnswersDefined: boolean;
}

export interface IAnswer extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly isNameDefined: boolean;
}

export interface IStringAnswer extends IAnswer, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericAnswer extends IAnswer, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export class Entity implements IEntity {
    public static readonly modelName: string = 'Entity';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityId', 'questions' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Entity.modelName;
    }

    private _entityId: string;

    private _questions: AQuestion[];

    public get entityId(): string {
        // Method Implementation: simple
        return this._entityId;
    }

    public get questions(): AQuestion[] {
        // Method Implementation: simple
        return this._questions;
    }

    public set entityId(value: string) {
        // Method Implementation: simple
        this._entityId = value;
        this._isEntityIdDefined = true;
    }

    public set questions(value: AQuestion[]) {
        // Method Implementation: simple
        this._questions = value;
        this._isQuestionsDefined = true;
    }

    private _isEntityIdDefined: boolean;

    private _isQuestionsDefined: boolean;

    public get isEntityIdDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityIdDefined;
    }

    public get isQuestionsDefined(): boolean {
        // Method Implementation: simple
        return this._isQuestionsDefined;
    }

    public constructor();
    public constructor(source: IEntity);
    public constructor(source: Partial<Pick<Entity, 'entityId' | 'questions'>>);
    public constructor(source?: IEntity | Partial<Pick<Entity, 'entityId' | 'questions'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityModel(source)) {
                if (source.isEntityIdDefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.isQuestionsDefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
            else {
                if (source.entityId !== undefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.questions !== undefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
        }
        else {
            this._entityId = '';
            this._isEntityIdDefined = false;
        
            this._questions = [];
            this._isQuestionsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Entity.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityIdDefined && this.entityId !== undefined)
            definedProperties.push('entityId');
        if (this.isQuestionsDefined && this.questions !== undefined)
            definedProperties.push('questions');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityId':
                return this.entityId;
            case 'questions':
                return this.questions;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            if (this.entityId !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityId);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'null'));
            }
        }
        
        if (this.isQuestionsDefined && this.questions !== undefined) {
            if (this.questions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.questions.length));
                for (let i = 0; i < this.questions.length; i++) {
                    const item = this.questions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityId':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityId = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityId' cannot be null`);
                    this.entityId = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityId'`);
                }
        
                return true;
            case 'questions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.questions = [];
                
                    let item: AQuestion = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'questions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'questions'`);
                        }
                
                        this.questions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'questions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'questions'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityId
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityId === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityId);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // questions
        if (this.isQuestionsDefined && this.questions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.questions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.questions.length);
            
                for (const item of this.questions) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof StringQuestion)
                            writer.writeShortValue(0);
                        else if (item instanceof NumericQuestion)
                            writer.writeShortValue(1);
                        else if (item instanceof BooleanQuestion)
                            writer.writeShortValue(2);
                        else if (item instanceof StringEnumerableQuestion)
                            writer.writeShortValue(3);
                        else if (item instanceof NumericEnumerableQuestion)
                            writer.writeShortValue(4);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityId
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityId = <any>null;
                    break;
                case 1:
                    this.entityId = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // questions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: AQuestion;
            
                    this.questions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new StringQuestion();
                                        break;
                                    case 1:
                                        item = new NumericQuestion();
                                        break;
                                    case 2:
                                        item = new BooleanQuestion();
                                        break;
                                    case 3:
                                        item = new StringEnumerableQuestion();
                                        break;
                                    case 4:
                                        item = new NumericEnumerableQuestion();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.questions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Entity {
        // Method Implementation: simple
        const schema = EntityZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityZodSchemaShape): Entity {
        // Method Implementation: parseZodSchema
        return new Entity({
            entityId: schema.entityId,
            questions: schema.questions?.map(i => AQuestion.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityId: this.isEntityIdDefined ? ((this.entityId === null ? undefined : this.entityId)) : undefined,
            questions: this.isQuestionsDefined ? (this.questions?.map(item => item)) : undefined,
        };
    }

    public clone(): Entity {
        // Method Implementation: clone
        return new Entity({
            entityId: this.isEntityIdDefined ? this.entityId : undefined,
            questions: this.isQuestionsDefined ? this.questions?.map(i => i.clone()) : undefined
        });
    }
}

export abstract class AQuestion implements IQuestion {
    public static readonly modelName: string = 'Question';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AQuestion.modelName;
    }

    private _name: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    private _isNameDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public constructor();
    public constructor(source: IQuestion);
    public constructor(source: Partial<Pick<AQuestion, 'name'>>);
    public constructor(source?: IQuestion | Partial<Pick<AQuestion, 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isQuestionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AQuestion.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AQuestion {
        // Method Implementation: simple
        const schema = QuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: QuestionZodSchemaShape): AQuestion {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.questionType)
        {
            case 'stringQuestion':
            case 'string':
                return StringQuestion.parse(schema);
            case 'numericQuestion':
            case 'numeric':
                return NumericQuestion.parse(schema);
            case 'booleanQuestion':
            case 'boolean':
                return BooleanQuestion.parse(schema);
            case 'stringEnumerableQuestion':
            case 'stringEnumerable':
                return StringEnumerableQuestion.parse(schema);
            case 'numericEnumerableQuestion':
            case 'numericEnumerable':
                return NumericEnumerableQuestion.parse(schema);
            default:
                throw new Error(`questionType of ${ schema.questionType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
        };
    }

    public abstract clone(): AQuestion;
}

export class StringQuestion extends AQuestion implements IStringQuestion {
    public static readonly modelName: string = 'StringQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringQuestion.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringQuestion);
    public constructor(source: Partial<Pick<StringQuestion, 'value' | 'name'>>);
    public constructor(source?: IStringQuestion | Partial<Pick<StringQuestion, 'value' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringQuestion {
        // Method Implementation: simple
        const schema = StringQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringQuestionZodSchemaShape): StringQuestion {
        // Method Implementation: parseZodSchema
        return new StringQuestion({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringQuestion {
        // Method Implementation: clone
        return new StringQuestion({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericQuestion extends AQuestion implements INumericQuestion {
    public static readonly modelName: string = 'NumericQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericQuestion.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: INumericQuestion);
    public constructor(source: Partial<Pick<NumericQuestion, 'value' | 'name'>>);
    public constructor(source?: INumericQuestion | Partial<Pick<NumericQuestion, 'value' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericQuestion {
        // Method Implementation: simple
        const schema = NumericQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericQuestionZodSchemaShape): NumericQuestion {
        // Method Implementation: parseZodSchema
        return new NumericQuestion({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): NumericQuestion {
        // Method Implementation: clone
        return new NumericQuestion({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class BooleanQuestion extends AQuestion implements IBooleanQuestion {
    public static readonly modelName: string = 'BooleanQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return BooleanQuestion.modelName;
    }

    private _value: boolean;

    public get value(): boolean {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: boolean) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IBooleanQuestion);
    public constructor(source: Partial<Pick<BooleanQuestion, 'value' | 'name'>>);
    public constructor(source?: IBooleanQuestion | Partial<Pick<BooleanQuestion, 'value' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isBooleanQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = false;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...BooleanQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.value = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.value = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): BooleanQuestion {
        // Method Implementation: simple
        const schema = BooleanQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: BooleanQuestionZodSchemaShape): BooleanQuestion {
        // Method Implementation: parseZodSchema
        return new BooleanQuestion({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'boolean',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): BooleanQuestion {
        // Method Implementation: clone
        return new BooleanQuestion({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class StringEnumerableQuestion extends AQuestion implements IStringEnumerableQuestion {
    public static readonly modelName: string = 'StringEnumerableQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'answers' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringEnumerableQuestion.modelName;
    }

    private _value: string;

    private _answers: StringAnswer[];

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public get answers(): StringAnswer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set answers(value: StringAnswer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    private _isValueDefined: boolean;

    private _isAnswersDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public constructor();
    public constructor(source: IStringEnumerableQuestion);
    public constructor(source: Partial<Pick<StringEnumerableQuestion, 'value' | 'answers' | 'name'>>);
    public constructor(source?: IStringEnumerableQuestion | Partial<Pick<StringEnumerableQuestion, 'value' | 'answers' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringEnumerableQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringEnumerableQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'answers':
                return this.answers;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: StringAnswer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === StringAnswer.modelName) {
                                item = new StringAnswer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${StringAnswer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: StringAnswer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new StringAnswer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringEnumerableQuestion {
        // Method Implementation: simple
        const schema = StringEnumerableQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringEnumerableQuestionZodSchemaShape): StringEnumerableQuestion {
        // Method Implementation: parseZodSchema
        return new StringEnumerableQuestion({
            name: schema.name,
            value: schema.value,
            answers: schema.answers?.map(i => StringAnswer.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'stringEnumerable',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
        };
    }

    public clone(): StringEnumerableQuestion {
        // Method Implementation: clone
        return new StringEnumerableQuestion({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined
        });
    }
}

export class NumericEnumerableQuestion extends AQuestion implements INumericEnumerableQuestion {
    public static readonly modelName: string = 'NumericEnumerableQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'answers' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericEnumerableQuestion.modelName;
    }

    private _value: number;

    private _answers: NumericAnswer[];

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public get answers(): NumericAnswer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set answers(value: NumericAnswer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    private _isValueDefined: boolean;

    private _isAnswersDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public constructor();
    public constructor(source: INumericEnumerableQuestion);
    public constructor(source: Partial<Pick<NumericEnumerableQuestion, 'value' | 'answers' | 'name'>>);
    public constructor(source?: INumericEnumerableQuestion | Partial<Pick<NumericEnumerableQuestion, 'value' | 'answers' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericEnumerableQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericEnumerableQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'answers':
                return this.answers;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: NumericAnswer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === NumericAnswer.modelName) {
                                item = new NumericAnswer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${NumericAnswer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: NumericAnswer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new NumericAnswer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): NumericEnumerableQuestion {
        // Method Implementation: simple
        const schema = NumericEnumerableQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericEnumerableQuestionZodSchemaShape): NumericEnumerableQuestion {
        // Method Implementation: parseZodSchema
        return new NumericEnumerableQuestion({
            name: schema.name,
            value: schema.value,
            answers: schema.answers?.map(i => NumericAnswer.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'numericEnumerable',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
        };
    }

    public clone(): NumericEnumerableQuestion {
        // Method Implementation: clone
        return new NumericEnumerableQuestion({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined
        });
    }
}

export abstract class AAnswer implements IAnswer {
    public static readonly modelName: string = 'Answer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AAnswer.modelName;
    }

    private _name: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    private _isNameDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public constructor();
    public constructor(source: IAnswer);
    public constructor(source: Partial<Pick<AAnswer, 'name'>>);
    public constructor(source?: IAnswer | Partial<Pick<AAnswer, 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isAnswerModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AAnswer.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AAnswer {
        // Method Implementation: simple
        const schema = AnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: AnswerZodSchemaShape): AAnswer {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.answerType)
        {
            case 'stringAnswer':
            case 'string':
                return StringAnswer.parse(schema);
            case 'numericAnswer':
            case 'numeric':
                return NumericAnswer.parse(schema);
            default:
                throw new Error(`answerType of ${ schema.answerType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
        };
    }

    public abstract clone(): AAnswer;
}

export class StringAnswer extends AAnswer implements IStringAnswer {
    public static readonly modelName: string = 'StringAnswer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringAnswer.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringAnswer);
    public constructor(source: Partial<Pick<StringAnswer, 'value' | 'name'>>);
    public constructor(source?: IStringAnswer | Partial<Pick<StringAnswer, 'value' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringAnswerModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringAnswer.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringAnswer {
        // Method Implementation: simple
        const schema = StringAnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringAnswerZodSchemaShape): StringAnswer {
        // Method Implementation: parseZodSchema
        return new StringAnswer({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            answerType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringAnswer {
        // Method Implementation: clone
        return new StringAnswer({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericAnswer extends AAnswer implements INumericAnswer {
    public static readonly modelName: string = 'NumericAnswer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericAnswer.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: INumericAnswer);
    public constructor(source: Partial<Pick<NumericAnswer, 'value' | 'name'>>);
    public constructor(source?: INumericAnswer | Partial<Pick<NumericAnswer, 'value' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericAnswerModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericAnswer.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericAnswer {
        // Method Implementation: simple
        const schema = NumericAnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericAnswerZodSchemaShape): NumericAnswer {
        // Method Implementation: parseZodSchema
        return new NumericAnswer({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            answerType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): NumericAnswer {
        // Method Implementation: clone
        return new NumericAnswer({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Entity':
                return new Entity();
            case 'StringQuestion':
                return new StringQuestion();
            case 'NumericQuestion':
                return new NumericQuestion();
            case 'BooleanQuestion':
                return new BooleanQuestion();
            case 'StringEnumerableQuestion':
                return new StringEnumerableQuestion();
            case 'NumericEnumerableQuestion':
                return new NumericEnumerableQuestion();
            case 'StringAnswer':
                return new StringAnswer();
            case 'NumericAnswer':
                return new NumericAnswer();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Entity':
                return new Entity(source);
            case 'StringQuestion':
                return new StringQuestion(source);
            case 'NumericQuestion':
                return new NumericQuestion(source);
            case 'BooleanQuestion':
                return new BooleanQuestion(source);
            case 'StringEnumerableQuestion':
                return new StringEnumerableQuestion(source);
            case 'NumericEnumerableQuestion':
                return new NumericEnumerableQuestion(source);
            case 'StringAnswer':
                return new StringAnswer(source);
            case 'NumericAnswer':
                return new NumericAnswer(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Entity':
                return Entity.parse(source);
            case 'StringQuestion':
                return StringQuestion.parse(source);
            case 'NumericQuestion':
                return NumericQuestion.parse(source);
            case 'BooleanQuestion':
                return BooleanQuestion.parse(source);
            case 'StringEnumerableQuestion':
                return StringEnumerableQuestion.parse(source);
            case 'NumericEnumerableQuestion':
                return NumericEnumerableQuestion.parse(source);
            case 'StringAnswer':
                return StringAnswer.parse(source);
            case 'NumericAnswer':
                return NumericAnswer.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
